import { jsPDF } from "jspdf";
import autoTable, { RowInput, CellDef } from "jspdf-autotable";
import { DiffResult } from "./diff";
import { displayKey, keyOf } from "../types/state";

const EMERALD: [number, number, number] = [16, 122, 87];
const EMERALD_BG: [number, number, number] = [220, 252, 231];
const AMBER: [number, number, number] = [146, 94, 13];
const AMBER_BG: [number, number, number] = [254, 243, 199];
const ROSE: [number, number, number] = [159, 27, 64];
const ROSE_BG: [number, number, number] = [254, 226, 226];
const STONE_700: [number, number, number] = [68, 64, 60];
const STONE_500: [number, number, number] = [120, 113, 108];

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

export function exportDiffPdf(
  diff: DiffResult,
  referenceLabel: string,
  keyColumns: string[],
  listTitle: string
): void {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 12;

  doc.setFont("times", "normal");
  doc.setFontSize(18);
  doc.setTextColor(...STONE_700);
  doc.text(listTitle, margin, 18);

  doc.setFontSize(10);
  doc.setTextColor(...STONE_500);
  doc.text(
    `Récapitulatif des changements · Référence : ${referenceLabel} · Généré le ${formatDate(new Date().toISOString())}`,
    margin,
    25
  );

  const total = diff.added.length + diff.modified.length + diff.removed.length;
  doc.setFontSize(11);
  doc.setTextColor(...STONE_700);
  doc.text(
    `${total} changement${total > 1 ? "s" : ""} · ${diff.added.length} ajout${diff.added.length > 1 ? "s" : ""} · ${diff.modified.length} modif${diff.modified.length > 1 ? "s" : ""} · ${diff.removed.length} retrait${diff.removed.length > 1 ? "s" : ""}`,
    margin,
    32
  );

  const colW = (pageW - margin * 2 - 10) / 3;
  const colXs = [margin, margin + colW + 5, margin + (colW + 5) * 2];
  const colTitles = [
    { title: `Ajouts (${diff.added.length})`, color: EMERALD },
    { title: `Modifs (${diff.modified.length})`, color: AMBER },
    { title: `Retraits (${diff.removed.length})`, color: ROSE },
  ];

  const colBodies: RowInput[][] = [
    diff.added.map((r) => [formatRowSummary(r, keyColumns)]),
    diff.modified.map((m) => [formatModifiedSummary(displayKey(m.row, keyColumns) || m.key, m.changes)]),
    diff.removed.map((r) => [formatRowSummary(r, keyColumns)]),
  ];

  const startY = 38;
  for (let i = 0; i < 3; i++) {
    doc.setFontSize(11);
    doc.setTextColor(...colTitles[i].color);
    doc.text(colTitles[i].title, colXs[i], startY);

    if (colBodies[i].length === 0) {
      doc.setFontSize(9);
      doc.setTextColor(...STONE_500);
      doc.text("Aucun", colXs[i], startY + 6);
      continue;
    }

    autoTable(doc, {
      startY: startY + 3,
      margin: { left: colXs[i], right: pageW - colXs[i] - colW },
      body: colBodies[i],
      theme: "plain",
      styles: { fontSize: 8, cellPadding: 2, textColor: STONE_700, lineColor: STONE_500, lineWidth: 0.1 },
      tableWidth: colW,
    });
  }

  addFooter(doc);
  doc.save(`diff-${today()}.pdf`);
}

function formatRowSummary(row: Record<string, string>, keyColumns: string[]): string {
  const label = displayKey(row, keyColumns) || "(sans clé)";
  const keySet = new Set(keyColumns);
  const other = Object.entries(row)
    .filter(([k, v]) => !keySet.has(k) && v)
    .slice(0, 3)
    .map(([, v]) => truncate(v, 40))
    .join(" · ");
  return other ? `${label} — ${other}` : label;
}

function formatModifiedSummary(key: string, changes: Record<string, { before: string; after: string }>): string {
  const lines: string[] = [key];
  for (const [col, { before, after }] of Object.entries(changes)) {
    lines.push(`  ${col}: ${truncate(before, 30)} → ${truncate(after, 30)}`);
  }
  return lines.join("\n");
}

export function exportFullPdf(
  diff: DiffResult,
  currentRows: Record<string, string>[],
  columns: string[],
  referenceLabel: string,
  keyColumns: string[],
  listTitle: string
): void {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 12;

  doc.setFont("times", "normal");
  doc.setFontSize(18);
  doc.setTextColor(...STONE_700);
  doc.text(listTitle, margin, 18);

  doc.setFontSize(10);
  doc.setTextColor(...STONE_500);
  doc.text(
    `Liste complète · Référence : ${referenceLabel} · Généré le ${formatDate(new Date().toISOString())}`,
    margin,
    25
  );

  doc.setFontSize(9);
  const legendY = 31;
  drawLegend(doc, margin, legendY, EMERALD_BG, "Ajoutée");
  drawLegend(doc, margin + 30, legendY, AMBER_BG, "Modifiée");
  drawLegend(doc, margin + 60, legendY, ROSE_BG, "Retirée");

  const addedKeys = new Set(diff.added.map((r) => keyOf(r, keyColumns)));
  const modifiedMap = new Map(diff.modified.map((m) => [m.key, m]));
  const removedRows = diff.removed;

  const head: RowInput[] = [columns.map((c) => ({ content: c, styles: { fontStyle: "bold" } }))];

  const body: RowInput[] = [];
  for (const row of currentRows) {
    const key = keyOf(row, keyColumns);
    const isAdded = addedKeys.has(key);
    const modified = modifiedMap.get(key);
    const rowCells: CellDef[] = columns.map((col) => {
      const cell: CellDef = { content: row[col] ?? "" };
      if (isAdded) {
        cell.styles = { fillColor: EMERALD_BG };
      } else if (modified && modified.changes[col]) {
        cell.styles = { fillColor: AMBER_BG };
      }
      return cell;
    });
    body.push(rowCells);
  }

  for (const row of removedRows) {
    const rowCells: CellDef[] = columns.map((col) => ({
      content: row[col] ?? "",
      styles: { fillColor: ROSE_BG, textColor: STONE_500 },
    }));
    body.push(rowCells);
  }

  autoTable(doc, {
    startY: 38,
    margin: { left: margin, right: margin },
    head,
    body,
    theme: "grid",
    styles: { fontSize: 7, cellPadding: 1.5, textColor: STONE_700, lineColor: [200, 200, 200], lineWidth: 0.1, overflow: "linebreak" },
    headStyles: { fillColor: [245, 245, 244], textColor: STONE_700, fontStyle: "bold" },
    tableWidth: pageW - margin * 2,
  });

  addFooter(doc);
  doc.save(`liste-${today()}.pdf`);
}

function drawLegend(doc: jsPDF, x: number, y: number, color: [number, number, number], label: string): void {
  doc.setFillColor(...color);
  doc.rect(x, y - 3, 4, 4, "F");
  doc.setTextColor(...STONE_700);
  doc.text(label, x + 6, y);
}

function addFooter(doc: jsPDF): void {
  const pageCount = doc.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...STONE_500);
    doc.text(`Page ${i} / ${pageCount}`, pageW - 20, pageH - 6);
  }
}
